// „Länge halbieren / verdoppeln" (Q/W) samt Reflow einer Stimme über Taktlinien. Eigenes Modul,
// weil der Reflow (Zählzeiten einer Stimme ab einem Takt bis Stückende neu auf die Takte verteilen,
// an Taktlinien teilen und binden) mehr ist als ein Struktur-Edit innerhalb eines Takts.
// Abhängigkeitsregel wie im ganzen Ordner: Basis-Helfer aus `core.ts`, gerichtete Geschwister-
// Imports (structure, notes, measureAttributes), keine Zyklen.
import {
    type NoteInfo,
    TYPE_LADDER,
    TYPE_QUARTER_LENGTH,
    chordGroupOf,
    doublePartDivisions,
    findPartAndMeasureNote,
    getPartDivisions,
    getVoiceNotes,
    noteDurationTicks,
    parseMusicXml,
    serializeMusicXml,
    ticksToRestElements,
    ticksToTypeAndDots,
    voiceOf,
} from './core';
import {expectedMeasureTicks} from './measureAttributes';
import {addTiedNotation, hasTie, insertTieElement, removeTieEnds} from './notes';
import {applyMeasureAttrs, deleteNotesInDoc, insertMeasureInDoc, setDurationTypeAndDots} from './structure';

/** Faktor für {@link scaleNoteDurationsInMusicXml}: halbieren bzw. verdoppeln. */
export type DurationScale = 0.5 | 2;

export interface ScaleDurationsResult {
    xml: string;
    /** Parallel zu den übergebenen `infos`: die Adresse der Note im **neuen** XML (Takt und
     *  `voiceNoteIndex` neu bestimmt – die Note kann in einen anderen Takt gewandert sein –,
     *  `noteIndex: -1` = nach dem Render frisch auflösen) oder `null`, wenn sie weggefallen ist. */
    selection: (NoteInfo | null)[];
}

/**
 * „Länge halbieren / verdoppeln" (Q/W): skaliert die Dauer jeder ausgewählten Zählzeit (Note, Pause,
 * alle Akkordtöne) und **verschiebt alles Folgende derselben Stimme** – es wird nichts überschrieben.
 *
 * - **Verdoppeln** schiebt die Folgenoten nach hinten, **Halbieren** zieht sie nach vorne. Ab dem
 *   Takt der ersten bearbeiteten Note bis zum Stückende werden die Zählzeiten der Stimme neu auf die
 *   Takte verteilt; eine Zählzeit, die über eine Taktlinie ragt, wird dort geteilt und gebunden
 *   (`<tie>` + `<tied>` je Akkordton; Gesangstext und Artikulation bleiben am ersten Teil).
 * - **Stückende:** Halbieren füllt die freigewordene Zeit als Pause am Stimmenende auf. Verdoppeln
 *   verbraucht zuerst **Pausen am Stimmenende** (nie Pausen mitten im Stück) und hängt erst danach
 *   Takte an (score-weit, andere Stimmen/Parts dort mit Pausen; ein Schlussstrich wandert mit). So
 *   sind Q und W exakt invers.
 * - **Nur die bearbeitete Stimme** verschiebt sich. Andere Stimmen/Parts, Akkordsymbole und
 *   Directions bleiben an ihrem Zeitpunkt im Takt.
 * - Die Takt-Kapazität ist die **tatsächliche** Taktlänge (Auftakt, kurzer Schlusstakt bleiben), für
 *   angehängte Takte das geltende Metrum.
 *
 * Übersprungen werden ausgewählte Vorschläge/Triolen und Halbierungen unter den kleinsten
 * Notenwert. `null` (nichts ändert sich), wenn keine Zählzeit skaliert wird oder der Reflow nicht
 * sicher möglich ist: Stimme im Takt nicht zusammenhängend bzw. nicht ab Taktanfang, eine Triole
 * müsste an einer Taktlinie geteilt werden, ein Teilstück ist nicht als Notenwert schreibbar.
 */
export function scaleNoteDurationsInMusicXml(
    musicXml: string, infos: readonly NoteInfo[], factor: DurationScale,
): ScaleDurationsResult | null {
    try {
        const doc = parseMusicXml(musicXml);
        const targets = infos.map(info => {
            const found = findPartAndMeasureNote(doc, info);
            return found ? {info, note: found.note, part: found.part} : null;
        });

        // Je (Part, Stimme) die ausgewählten Primärnoten – jede Gruppe ist ein eigener Reflow.
        const groups = new Map<Element, Map<number, Set<Element>>>();
        for (const t of targets) {
            if (!t) continue;
            const primary = chordGroupOf(t.note).find(n => !n.querySelector('grace'));
            if (!primary) continue;
            const byVoice = groups.get(t.part) ?? new Map<number, Set<Element>>();
            groups.set(t.part, byVoice);
            const set = byVoice.get(voiceOf(primary)) ?? new Set<Element>();
            byVoice.set(voiceOf(primary), set);
            set.add(primary);
        }

        let changed = false;
        for (const [part, byVoice] of groups) {
            for (const [voice, selected] of byVoice) {
                const result = reflowVoice(doc, part, voice, selected, factor);
                if (result === null) return null;
                if (result) changed = true;
            }
        }
        if (!changed) return null;

        const selection = targets.map(t => {
            if (!t || !t.note.isConnected) return null;
            const measure = t.note.closest('measure');
            if (!measure) return null;
            const measureIndex = [...t.part.querySelectorAll('measure')].indexOf(measure);
            const voiceNoteIndex = getVoiceNotes(measure, voiceOf(t.note)).indexOf(t.note);
            if (measureIndex < 0 || voiceNoteIndex < 0) return null;
            const measureNumber = parseInt(measure.getAttribute('number') || '', 10);
            return {
                ...t.info,
                measureIndex,
                measureNumber: Number.isNaN(measureNumber) ? t.info.measureNumber : measureNumber,
                voiceNoteIndex,
                noteIndex: -1,
            };
        });
        return {xml: serializeMusicXml(doc), selection};
    } catch (e) {
        console.error('Error scaling note durations in MusicXML:', e);
        return null;
    }
}

/**
 * Löschen **mit Nachrücken**: die ausgewählten Noten werden wie bei `deleteNotesInMusicXml` gelöscht
 * (Akkordton mit verbleibenden Nachbarn ersatzlos, Bögen gelöst). Jede dabei **ganz** entfallene
 * Zählzeit wird anschließend aus ihrer Stimme genommen, und alles Folgende derselben Stimme rückt
 * nach vorne – über Taktlinien, mit derselben Mechanik wie Q/W ({@link scaleNoteDurationsInMusicXml}:
 * an Taktlinien geteilt und gebunden, Endpause füllt auf, andere Stimmen/Parts, Akkordsymbole und
 * Directions bleiben an ihrem Zeitpunkt).
 *
 * `null` = nichts geändert: keine Zählzeit entfällt ganz, eine entfallene Zählzeit ist Teil einer
 * Triole (die Gruppe bliebe unvollständig), oder der Reflow ist nicht sicher möglich (Gründe wie bei
 * Q/W). Der Aufrufer bietet dann das längenerhaltende Löschen an.
 */
export function deleteNotesAndShiftInMusicXml(musicXml: string, infos: readonly NoteInfo[]): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const cleared = deleteNotesInDoc(doc, infos);
        if (!cleared.length) return null;
        if (cleared.some(n => n.querySelector(':scope > time-modification'))) return null;

        // Je (Part, Stimme) ein Reflow – Muster aus `scaleNoteDurationsInMusicXml`.
        const groups = new Map<Element, Map<number, Map<Element, number>>>();
        for (const primary of cleared) {
            const part = primary.closest('part');
            if (!part) return null;
            const byVoice = groups.get(part) ?? new Map<number, Map<Element, number>>();
            groups.set(part, byVoice);
            const ticks = byVoice.get(voiceOf(primary)) ?? new Map<Element, number>();
            byVoice.set(voiceOf(primary), ticks);
            ticks.set(primary, 0);
        }
        for (const [part, byVoice] of groups) {
            for (const [voice, ticks] of byVoice) {
                if (!reflowVoiceWithTicks(doc, part, voice, ticks)) return null;
            }
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error deleting notes with shift in MusicXML:', e);
        return null;
    }
}

// ============ Zählzeiten einer Stimme lesen ============

/** Eine Zählzeit der Stimme. `elements` = Vorschläge + Primärnote + Akkord-Begleiter in
 *  Dokumentreihenfolge; leer = Pause, die beim Auslegen neu gebaut wird (Füllpause, `<forward>`). */
interface Beat {
    elements: Element[];
    primary: Element | null;
    ticks: number;
    tuplet: boolean;
}

/** Ein Takt aus Sicht der Stimme: wo ihr Abschnitt liegt, was darin an Nicht-Noten steht (mit
 *  Offset, damit es an seinem Zeitpunkt bleibt) und wie lang der Takt ist. */
interface MeasureSlot {
    measure: Element;
    capacity: number;
    beats: Beat[];
    /** Elemente der Stimme (Noten, eigene `<forward>`) – werden beim Auslegen ersetzt. */
    voiceElements: Element[];
    /** Nicht-Noten-Elemente zwischen erster und letzter Stimmen-Note, mit Offset ab Taktanfang. */
    kept: { el: Element; offset: number }[];
    /** Erstes `<backup>` hinter dem Abschnitt (wird um die Längenänderung der Stimme korrigiert). */
    nextBackup: Element | null;
    oldVoiceTicks: number;
    /** Cursor am Taktende – Stimme fehlt im Takt: so weit muss vor ihren neuen Noten zurück. */
    cursorEnd: number;
}

/** `<offset>` eines `<harmony>`/`<direction>` (Ticks relativ zur Dokumentposition), sonst 0. */
const ownOffset = (el: Element): number => parseInt(el.querySelector(':scope > offset')?.textContent || '0', 10) || 0;

/** Setzt/entfernt das `<offset>` eines Abschnitts-Elements schema-konform (vor footnote/level/voice/
 *  staff/sound). Nur `<harmony>` und `<direction>` kennen es; andere Elemente bleiben unverändert. */
function setOwnOffset(doc: Document, el: Element, ticks: number): void {
    if (!['harmony', 'direction'].includes(el.tagName.toLowerCase())) return;
    el.querySelector(':scope > offset')?.remove();
    if (ticks <= 0) return;
    const offset = doc.createElement('offset');
    offset.textContent = String(ticks);
    const before = [...el.children].find(c => ['footnote', 'level', 'voice', 'staff', 'sound'].includes(c.tagName.toLowerCase()));
    if (before) before.before(offset); else el.appendChild(offset);
}

const durationOf = (el: Element): number => parseInt(el.querySelector(':scope > duration')?.textContent || '0', 10);
const forwardVoice = (el: Element): number | null => {
    const v = el.querySelector(':scope > voice')?.textContent;
    return v ? parseInt(v, 10) : null;
};

/** Analysiert einen Takt für die Stimme; `null`, wenn ihr Abschnitt nicht zusammenhängend ist, nicht
 *  am Taktanfang beginnt oder eine Längenänderung hinter ihm nicht korrigierbar wäre. */
function analyzeMeasure(measure: Element, voice: number, meterTicks: number): MeasureSlot | null {
    let cursor = 0;
    let maxCursor = 0;
    let state: 'before' | 'in' | 'after' = 'before';
    let pendingGraces: Element[] = [];
    let current: Beat | null = null;
    let nextTimedAfter: Element | null = null;
    const beats: Beat[] = [];
    const voiceElements: Element[] = [];
    const between: { el: Element; offset: number; index: number }[] = [];
    let lastVoiceIndex = -1;

    for (const [index, child] of [...measure.children].entries()) {
        const tag = child.tagName.toLowerCase();
        const ours = (tag === 'note' && voiceOf(child) === voice) || (tag === 'forward' && forwardVoice(child) === voice);
        const timed = tag === 'note' || tag === 'backup' || tag === 'forward';

        if (ours) {
            if (state === 'after') return null;                  // Stimme in mehreren Abschnitten
            if (state === 'before' && cursor !== 0) return null; // beginnt nicht am Taktanfang
            state = 'in';
            voiceElements.push(child);
            lastVoiceIndex = index;
            if (tag === 'forward') {
                current = {elements: [], primary: null, ticks: durationOf(child), tuplet: false};
                beats.push(current);
                cursor += durationOf(child);
            } else if (child.querySelector(':scope > grace')) {
                pendingGraces.push(child);
            } else if (child.querySelector(':scope > chord')) {
                current?.elements.push(child);
            } else {
                current = {
                    elements: [...pendingGraces, child],
                    primary: child,
                    ticks: durationOf(child),
                    tuplet: !!child.querySelector(':scope > time-modification'),
                };
                pendingGraces = [];
                beats.push(current);
                cursor += durationOf(child);
            }
        } else if (timed) {
            if (state === 'in') state = 'after';
            if (state === 'after' && !nextTimedAfter) nextTimedAfter = child;
            if (tag === 'backup') cursor -= durationOf(child);
            else if (tag === 'forward') cursor += durationOf(child);
            else if (!child.querySelector(':scope > chord') && !child.querySelector(':scope > grace')) {
                cursor += durationOf(child);
            }
        } else if (state === 'in') {
            between.push({el: child, offset: cursor + ownOffset(child), index});
        }
        maxCursor = Math.max(maxCursor, cursor);
    }
    // Vorschläge am Abschnittsende hängen an der letzten Zählzeit (selten, nur Ordnung).
    if (pendingGraces.length) {
        if (!current) return null;
        current.elements.push(...pendingGraces);
    }

    // Nur Nicht-Noten ZWISCHEN den Stimmen-Noten gehören zum Abschnitt; was hinter der letzten steht
    // (Barline, Direction am Taktende), bleibt an seinem Platz.
    // Nach Zeitpunkt (stabil): ein `<offset>` macht die Dokumentreihenfolge nicht mehr monoton.
    const kept = between.filter(b => b.index < lastVoiceIndex)
        .map(({el, offset}) => ({el, offset}))
        .sort((a, b) => a.offset - b.offset);

    const capacity = maxCursor > 0 ? maxCursor : meterTicks;
    const oldVoiceTicks = beats.reduce((s, b) => s + b.ticks, 0);
    if (oldVoiceTicks > capacity) return null;
    if (oldVoiceTicks < capacity) beats.push({elements: [], primary: null, ticks: capacity - oldVoiceTicks, tuplet: false});

    // Hinter dem Abschnitt darf nur ein `<backup>` folgen, wenn sich die Stimmenlänge ändert.
    const nextBackup = nextTimedAfter?.tagName.toLowerCase() === 'backup' ? nextTimedAfter : null;
    if (nextTimedAfter && !nextBackup && oldVoiceTicks !== capacity && voiceElements.length) return null;

    return {measure, capacity, beats, voiceElements, kept, nextBackup, oldVoiceTicks, cursorEnd: cursor};
}

/** Erwartete Ticks je Takt nach dem am Takt `i` geltenden Metrum (Kaskade aus `<time>`). */
function meterTicksPerMeasure(part: Element, divisions: number): number[] {
    const state = new Map<string, Element>();
    return [...part.querySelectorAll('measure')].map(m => {
        applyMeasureAttrs(m, state);
        const time = state.get('time');
        const beats = parseInt(time?.querySelector('beats')?.textContent || '4', 10) || 4;
        const beatType = parseInt(time?.querySelector('beat-type')?.textContent || '4', 10) || 4;
        return expectedMeasureTicks(beats, beatType, divisions) || divisions * 4;
    });
}

/** Zerlegt `ticks` in schreibbare Notenwerte (0–1 Punkt, groß → klein); `null`, wenn ein Rest unter
 *  der Auflösung von `divisions` bliebe. Anders als `ticksToRestElements` nie ein genäherter Typ. */
export function decomposeTicks(ticks: number, divisions: number): { ticks: number; type: string; dots: number }[] | null {
    const exact = ticksToTypeAndDots(ticks, divisions);
    if (exact) return [{ticks, ...exact}];
    const out: { ticks: number; type: string; dots: number }[] = [];
    let remaining = ticks;
    for (const type of TYPE_LADDER) {
        for (const dots of [1, 0]) {
            const t = TYPE_QUARTER_LENGTH[type]! * (2 - Math.pow(2, -dots)) * divisions;
            if (!Number.isInteger(t) || t <= 0) continue;
            while (remaining >= t) {
                out.push({ticks: t, type, dots});
                remaining -= t;
            }
        }
    }
    return remaining === 0 ? out : null;
}

// ============ Reflow ============

/**
 * Skaliert die ausgewählten Zählzeiten einer Stimme und verteilt die Stimme ab dem Takt der ersten
 * davon neu. Rückgabe: `true` = geändert, `false` = nichts zu tun, `null` = Abbruch (Reflow nicht
 * sicher möglich – der Aufrufer verwirft das ganze Dokument).
 */
function reflowVoice(
    doc: Document, part: Element, voice: number, selected: Set<Element>, factor: DurationScale,
): boolean | null {
    const scalable = [...selected].filter(n => !n.querySelector(':scope > time-modification'));
    if (factor === 0.5) {
        for (let guard = 0; guard < 8 && scalable.some(n => noteDurationTicks(n) % 2 !== 0); guard++) {
            doublePartDivisions(part);
        }
    }
    const divisions = getPartDivisions(part) || 1;
    const scaledTicks = new Map<Element, number>();
    for (const n of scalable) {
        const t = noteDurationTicks(n) * factor;
        if (Number.isInteger(t) && t > 0 && decomposeTicks(t, divisions)) scaledTicks.set(n, t);
    }
    if (!scaledTicks.size) return false;
    return reflowVoiceWithTicks(doc, part, voice, scaledTicks);
}

/**
 * Gibt den Zählzeiten einer Stimme neue Längen (Primärnote → Ticks; **`0` = die Zählzeit entfällt**,
 * die Folgenden rücken nach) und verteilt die Stimme ab dem Takt der ersten davon neu. Gemeinsamer
 * Kern von Q/W ({@link reflowVoice}) und dem nachrückenden Löschen
 * ({@link deleteNotesAndShiftInMusicXml}). Rückgabe wie {@link reflowVoice}.
 */
function reflowVoiceWithTicks(
    doc: Document, part: Element, voice: number, scaledTicks: Map<Element, number>,
): boolean | null {
    const divisions = getPartDivisions(part) || 1;
    const measures = () => [...part.querySelectorAll('measure')];
    const startIndex = Math.min(...[...scaledTicks.keys()].map(n => measures().indexOf(n.closest('measure')!)));
    if (startIndex < 0) return null;
    // Alle Ticks werden in EINER Einheit gerechnet – ein abweichendes `<divisions>` im Bereich mischte sie.
    if (measures().slice(startIndex).some(m => [...m.querySelectorAll('divisions')]
        .some(d => parseInt(d.textContent || '0', 10) !== divisions))) return null;

    const read = (): { slots: MeasureSlot[]; beats: Beat[] } | null => {
        const meter = meterTicksPerMeasure(part, divisions);
        const slots: MeasureSlot[] = [];
        for (const [i, m] of measures().entries()) {
            if (i < startIndex) continue;
            const slot = analyzeMeasure(m, voice, meter[i]!);
            if (!slot) return null;
            slots.push(slot);
        }
        // Ticks 0 = Zählzeit entfällt: `layOut` löst alle Stimmen-Elemente des Bereichs und legt
        // nur die übergebenen Zählzeiten wieder ab – die entfallene kommt nicht zurück.
        const beats = slots.flatMap(s => s.beats).flatMap(b => {
            const t = b.primary ? scaledTicks.get(b.primary) : undefined;
            if (t === undefined) return [b];
            return t === 0 ? [] : [{...b, ticks: t}];
        });
        return {slots, beats};
    };

    let state = read();
    if (!state) return null;

    // Zu lang: erst Pausen am Stimmenende verbrauchen, dann Takte anhängen.
    const total = (bs: Beat[]) => bs.reduce((s, b) => s + b.ticks, 0);
    const capacity = (ss: MeasureSlot[]) => ss.reduce((s, x) => s + x.capacity, 0);
    const excess = total(state.beats) - capacity(state.slots) - trailingRestTicks(state.beats);
    if (excess > 0) {
        const meter = meterTicksPerMeasure(part, divisions);
        const lastCapacity = meter[meter.length - 1]!;
        const count = Math.ceil(excess / lastCapacity);
        for (let i = 0; i < count; i++) appendMeasureMovingFinalBarline(doc);
        state = read();
        if (!state) return null;
    }

    const {slots} = state;
    const beats = [...state.beats];
    let diff = total(beats) - capacity(slots);
    while (diff > 0) {                                       // Endpausen kürzen/verbrauchen
        const last = beats[beats.length - 1];
        if (!last || last.primary && !last.primary.querySelector(':scope > rest') || last.tuplet) return null;
        if (last.ticks <= diff) {
            beats.pop();
            diff -= last.ticks;
        } else {
            beats[beats.length - 1] = {...last, ticks: last.ticks - diff};
            diff = 0;
        }
    }
    if (diff < 0) beats.push({elements: [], primary: null, ticks: -diff, tuplet: false});

    return layOut(doc, slots, beats, voice, divisions, voiceStaff(slots)) ? true : null;
}

/** Ticks der Pausen am Ende der Stimme (nach der letzten klingenden Zählzeit). */
function trailingRestTicks(beats: Beat[]): number {
    let sum = 0;
    for (let i = beats.length - 1; i >= 0; i--) {
        const b = beats[i]!;
        if (b.tuplet || (b.primary && !b.primary.querySelector(':scope > rest'))) break;
        sum += b.ticks;
    }
    return sum;
}

/** `<staff>` der Stimme (Grand-Staff-Pflicht für neu gebaute Noten), `null` bei Einzel-Staff. */
function voiceStaff(slots: MeasureSlot[]): string | null {
    for (const s of slots) {
        const staff = s.voiceElements.find(e => e.tagName.toLowerCase() === 'note')?.querySelector(':scope > staff')?.textContent;
        if (staff) return staff;
    }
    return null;
}

/** Hängt score-weit einen Pausentakt an und verschiebt einen Schlussstrich (rechte Barline ohne
 *  Wiederholung) des bisherigen letzten Takts in den neuen. */
function appendMeasureMovingFinalBarline(doc: Document): void {
    const parts = [...doc.querySelectorAll('part')];
    const lastIndex = (parts[0]?.querySelectorAll('measure').length ?? 1) - 1;
    insertMeasureInDoc(doc, lastIndex, 'after');
    for (const part of parts) {
        const ms = [...part.querySelectorAll('measure')];
        const oldLast = ms[ms.length - 2];
        const newLast = ms[ms.length - 1];
        if (!oldLast || !newLast) continue;
        const barline = oldLast.querySelector(':scope > barline[location="right"]');
        if (barline && !barline.querySelector('repeat')) newLast.appendChild(barline);
    }
}

/** Ein ausgelegtes Stück: Elemente einer Zählzeit (oder ihres Teils) und sein Onset im Takt. */
interface Placed {
    elements: Element[];
    onset: number;
    ticks: number;
}

/**
 * Schreibt die Zählzeiten neu in die Takte. Erst werden in **allen** Takten die Stimmen-Elemente und
 * der Abschnitts-Inhalt herausgelöst (weggefallene Endpausen tauchen so nicht wieder auf), dann wird
 * Takt für Takt gefüllt. `false` = ein Teilstück ist nicht schreibbar oder eine Triole müsste geteilt
 * werden.
 */
function layOut(
    doc: Document, slots: MeasureSlot[], beats: Beat[], voice: number, divisions: number, staff: string | null,
): boolean {
    // Platzhalter an der Stelle des Abschnitts setzen und Stimme + Abschnitts-Inhalt lösen.
    const anchors = slots.map(slot => {
        const marker = doc.createComment('reflow');
        const first = slot.voiceElements[0];
        if (first) {
            first.before(marker);
        } else {
            const rightBarline = slot.measure.querySelector(':scope > barline[location="right"]');
            if (rightBarline) rightBarline.before(marker); else slot.measure.appendChild(marker);
            if (slot.cursorEnd > 0) marker.before(makeTimed(doc, 'backup', slot.cursorEnd));
        }
        for (const el of slot.voiceElements) el.remove();
        for (const {el} of slot.kept) el.remove();
        // Längenänderung der Stimme im Takt im folgenden `<backup>` ausgleichen.
        if (slot.nextBackup && slot.voiceElements.length) {
            const dur = slot.nextBackup.querySelector(':scope > duration');
            if (dur) dur.textContent = String(durationOf(slot.nextBackup) + slot.capacity - slot.oldVoiceTicks);
        }
        return marker;
    });

    let bi = 0;
    let consumed = 0;                                     // bereits ausgelegte Ticks von beats[bi]
    const outgoingTies = new Map<Beat, boolean[]>();      // je Akkordton: hatte ein ausgehendes tie-start
    let previousChunk: Element[] | null = null;

    for (const [si, slot] of slots.entries()) {
        const placed: Placed[] = [];
        let onset = 0;
        while (onset < slot.capacity) {
            const beat = beats[bi];
            if (!beat) return false;
            const take = Math.min(slot.capacity - onset, beat.ticks - consumed);
            const first = consumed === 0;
            const last = consumed + take === beat.ticks;
            if (beat.tuplet && !(first && last)) return false;

            const pitched = beat.elements.filter(e => !e.querySelector(':scope > grace') && !e.querySelector(':scope > rest'));
            if (first) {
                previousChunk = null;
                if (pitched.length) {
                    outgoingTies.set(beat, pitched.map(n => hasTie(n, 'start')));
                    for (const n of pitched) removeTieEnds(n, 'start');
                }
            }

            if (beat.tuplet) {
                placed.push({elements: beat.elements, onset, ticks: take});
                if (pitched.length) previousChunk = pitched;
            } else {
                const shapes = decomposeTicks(take, divisions);
                if (!shapes) return false;
                let shapeOnset = onset;
                for (const [k, shape] of shapes.entries()) {
                    const chunk = buildChunk(doc, beat, first && k === 0, shape, voice, divisions, staff);
                    if (pitched.length && previousChunk) {
                        for (const n of previousChunk) {
                            insertTieElement(doc, n, 'start');
                            addTiedNotation(doc, n, 'start');
                        }
                        for (const n of chunk.notes) {
                            insertTieElement(doc, n, 'stop');
                            addTiedNotation(doc, n, 'stop');
                        }
                    }
                    if (pitched.length) previousChunk = chunk.notes;
                    placed.push({elements: chunk.elements, onset: shapeOnset, ticks: shape.ticks});
                    shapeOnset += shape.ticks;
                }
            }

            if (last && previousChunk) {
                const ties = outgoingTies.get(beat) ?? [];
                previousChunk.forEach((n, i) => {
                    if (!ties[i]) return;
                    insertTieElement(doc, n, 'start');
                    addTiedNotation(doc, n, 'start');
                });
            }
            onset += take;
            consumed += take;
            if (last) {
                bi++;
                consumed = 0;
            }
        }

        // Abschnitts-Inhalt (Akkordsymbole, Directions …) an seinem Zeitpunkt wieder einsortieren: vor
        // das Stück, das diesen Zeitpunkt überdeckt, den Rest als `<offset>`. music21 (und damit die
        // Konvertierung) liest das `<offset>`; OSMD ignoriert es und zeigt das Symbol am Notenanfang.
        const marker = anchors[si]!;
        const kept = [...slot.kept];
        for (const piece of placed) {
            while (kept.length && kept[0]!.offset < piece.onset + piece.ticks) {
                const {el, offset} = kept.shift()!;
                setOwnOffset(doc, el, Math.max(0, offset - piece.onset));
                marker.before(el);
            }
            for (const el of piece.elements) marker.before(el);
        }
        for (const {el} of kept) marker.before(el);
        marker.remove();
    }
    return bi === beats.length;
}

const makeTimed = (doc: Document, tag: 'backup' | 'forward', ticks: number): Element => {
    const el = doc.createElement(tag);
    const dur = doc.createElement('duration');
    dur.textContent = String(ticks);
    el.appendChild(dur);
    return el;
};

/** Baut ein Teilstück einer Zählzeit mit der Dauer `shape`. Das erste Teilstück verwendet die
 *  Original-Elemente (Gesangstext, Artikulation, eingehende Bögen bleiben dort), jedes weitere ist
 *  ein bereinigter Klon. Neu zu bauende Pausen (leere Zählzeit) entstehen frisch. */
function buildChunk(
    doc: Document, beat: Beat, isFirst: boolean, shape: { ticks: number; type: string; dots: number },
    voice: number, divisions: number, staff: string | null,
): { elements: Element[]; notes: Element[] } {
    if (!beat.elements.length) {
        const rests = ticksToRestElements(doc, shape.ticks, divisions, voice, staff);
        return {elements: rests, notes: []};
    }
    const sounding = beat.elements.filter(e => !e.querySelector(':scope > grace'));
    const pitched = sounding.filter(e => !e.querySelector(':scope > rest'));
    if (isFirst) {
        for (const n of sounding) {
            for (const beam of [...n.querySelectorAll('beam')]) beam.remove();
            n.querySelector(':scope > rest')?.removeAttribute('measure');
            setDurationTypeAndDots(doc, n, shape.ticks, shape.type, shape.dots);
        }
        return {elements: beat.elements, notes: pitched};
    }
    const clones = sounding.map(n => {
        const clone = n.cloneNode(true) as Element;
        for (const el of [...clone.querySelectorAll('lyric, beam, tie, notations, accidental')]) el.remove();
        clone.querySelector(':scope > rest')?.removeAttribute('measure');
        setDurationTypeAndDots(doc, clone, shape.ticks, shape.type, shape.dots);
        return clone;
    });
    return {elements: clones, notes: clones.filter(c => !c.querySelector(':scope > rest'))};
}
